import * as THREE from 'three';
import { tuning } from '@/config/tuning';

/**
 * createRenderer — a WebGL2 renderer configured for a good-looking but
 * mobile-friendly result: antialiasing, soft shadows, ACES filmic tone mapping
 * and sRGB output (correct, un-washed-out colour), with the device pixel ratio
 * capped so high-density phone screens don't melt rendering 3x the pixels.
 */
export function createRenderer(): THREE.WebGLRenderer {
  const renderer = new THREE.WebGLRenderer({
    antialias: true,
    powerPreference: 'high-performance',
    alpha: false, // the scene paints a solid background; no transparent canvas
    stencil: false,
  });

  renderer.setPixelRatio(Math.min(window.devicePixelRatio, tuning.render.maxPixelRatio));
  renderer.setSize(window.innerWidth, window.innerHeight);

  // Soft shadows from a single directional "sun".
  // (three.js r182+ folded soft filtering into PCFShadowMap and deprecated the
  // old PCFSoftShadowMap constant, so PCFShadowMap is the current soft default.)
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFShadowMap;

  // Tone mapping + sRGB output = colours that look right. We use KHRONOS
  // PBR-Neutral (M35): it compresses highlights without desaturating, so the
  // hand-tuned city skies and vivid car/ball colours survive. NOTE: when the
  // PostFX composer is active it OWNS tone mapping (three.js ignores the
  // renderer's tone mapping when drawing into a render target) — PostFX sets this
  // to NoToneMapping then; this value is the fallback for the Low/no-post path.
  renderer.toneMapping = THREE.NeutralToneMapping;
  renderer.toneMappingExposure = tuning.render.toneMappingExposure;
  renderer.outputColorSpace = THREE.SRGBColorSpace;

  return renderer;
}

/**
 * Resize the renderer + camera to fill the current window. `ratioCap` is the
 * caller's pixel-ratio ceiling (the quality setting owns it — Low caps at 1) —
 * re-clamped against devicePixelRatio here since that can change when the
 * window moves between screens.
 */
export function resizeToWindow(
  renderer: THREE.WebGLRenderer,
  camera: THREE.PerspectiveCamera,
  ratioCap: number = tuning.render.maxPixelRatio,
): void {
  const w = window.innerWidth;
  const h = window.innerHeight;
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, ratioCap));
  renderer.setSize(w, h);
  camera.aspect = w / h;
  camera.updateProjectionMatrix();
}
